/**
 * Billing & Invoice Pulse Report Templates
 * Based on best practices from Stripe, Chargebee, Paddle
 */

import { PulseReportTemplate } from "./types";

export const INVOICE_SUMMARY: PulseReportTemplate = {
  id: "invoice-summary",
  name: "Invoice Summary",
  reportType: "invoice",
  description: "Professional invoice summary with payment confirmation",
  purpose:
    "Send immediately after successful payment to provide receipt and build trust. Reduces support tickets about payments.",
  icon: "FileTextOutlined",
  color: "#10b981",
  suggestedHtmlTemplateId: "invoice-summary",
  suggestedSubject: "Invoice #{{invoice_number}} - {{billing_amount}}",
  defaultSchedule: {
    type: "on_event",
    eventType: "on_refill",
  },
  defaultDataSections: {
    billing: true,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "All paying customers after successful payment",
  expectedOutcomes: [
    "Reduced payment-related support tickets by 60%",
    "Increased customer confidence in billing",
    "Clear payment documentation for customers",
    "Professional brand perception",
  ],
  bestPractices: [
    "Send immediately after payment success",
    "Include downloadable PDF invoice link",
    "Show next billing date clearly",
    "Make payment method visible for verification",
  ],
  keyMetrics: [
    "Invoice number",
    "Amount paid",
    "Payment method",
    "Billing period",
    "Next renewal date",
  ],
  tags: ["invoice", "payment", "receipt", "billing", "automated"],
  bestFor: ["SaaS subscriptions", "Recurring billing", "E-commerce"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Clean invoice with payment confirmation and PDF download",
};

export const PAYMENT_FAILED: PulseReportTemplate = {
  id: "payment-failed",
  name: "Payment Failed Alert",
  reportType: "invoice",
  description: "Urgent payment failure notification with recovery options",
  purpose:
    "Alert customers immediately when payment fails. Reduces involuntary churn by prompting quick action.",
  icon: "WarningOutlined",
  color: "#dc2626",
  suggestedHtmlTemplateId: "payment-failed",
  suggestedSubject: "Action required - Payment failed for {{billing_plan}}",
  defaultSchedule: {
    type: "on_event",
    eventType: "payment_failed",
  },
  defaultDataSections: {
    billing: true,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Customers with failed payment attempts",
  expectedOutcomes: [
    "70%+ payment recovery rate",
    "Reduced involuntary churn by 40%",
    "Quick customer action within 48 hours",
    "Clear communication of account status",
  ],
  bestPractices: [
    "Send within minutes of failure",
    "Show clear failure reason if available",
    "Provide one-click payment update link",
    "Include deadline for account suspension",
    "Follow up with reminders if not resolved",
  ],
  keyMetrics: [
    "Failed amount",
    "Failure reason",
    "Days until suspension",
    "Current payment method",
    "Update payment link",
  ],
  tags: ["payment", "failed", "dunning", "urgent", "recovery"],
  bestFor: ["Subscription recovery", "Churn prevention", "Dunning campaigns"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Urgent alert with clear CTA to update payment method",
};

export const SUBSCRIPTION_RENEWAL: PulseReportTemplate = {
  id: "subscription-renewal",
  name: "Subscription Renewal Notice",
  reportType: "invoice",
  description: "Upcoming renewal notification with plan summary",
  purpose:
    "Notify customers before automatic renewal to reduce chargebacks and build transparency.",
  icon: "SyncOutlined",
  color: "#0ea5e9",
  suggestedHtmlTemplateId: "subscription-renewal",
  suggestedSubject: "Your subscription renews on {{billing_next_renewal}}",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 25,
    time: "09:00",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Active subscribers 7 days before renewal",
  expectedOutcomes: [
    "Reduced chargebacks by 80%",
    "Fewer surprise billing complaints",
    "Opportunity for upsell before renewal",
    "Increased trust and transparency",
  ],
  bestPractices: [
    "Send 7 days before renewal date",
    "Show what they're paying for (plan features)",
    "Include option to modify or cancel",
    "Highlight value received in current period",
  ],
  keyMetrics: [
    "Renewal date",
    "Renewal amount",
    "Plan name",
    "Features included",
    "Manage subscription link",
  ],
  tags: ["renewal", "subscription", "reminder", "billing", "notification"],
  bestFor: ["Monthly subscriptions", "Annual renewals", "Enterprise accounts"],
  difficulty: "beginner",
  setupTime: "5 minutes",
  previewDescription: "Transparent renewal notice with plan details",
};

export const UPGRADE_CONFIRMATION: PulseReportTemplate = {
  id: "upgrade-confirmation",
  name: "Plan Upgrade Confirmation",
  reportType: "invoice",
  description: "Celebrate plan upgrades and highlight new features",
  purpose:
    "Confirm successful upgrade, celebrate the decision, and guide users to new features.",
  icon: "RocketOutlined",
  color: "#8b5cf6",
  suggestedHtmlTemplateId: "subscription-renewal",
  suggestedSubject: "Welcome to {{billing_plan}}! Here's what's new",
  defaultSchedule: {
    type: "on_event",
    eventType: "plan_upgraded",
  },
  defaultDataSections: {
    billing: true,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Customers who just upgraded their plan",
  expectedOutcomes: [
    "Faster adoption of premium features",
    "Reduced post-purchase anxiety",
    "Higher feature utilization rate",
    "Improved customer satisfaction",
  ],
  bestPractices: [
    "Celebrate their upgrade decision",
    "Highlight top 3 new features",
    "Provide quick-start guide for premium features",
    "Include support contact for premium users",
  ],
  keyMetrics: [
    "New plan name",
    "New features unlocked",
    "New limits",
    "Effective date",
    "Support priority level",
  ],
  tags: ["upgrade", "celebration", "premium", "features", "billing"],
  bestFor: ["Freemium to paid", "Plan upgrades", "Enterprise transitions"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Celebratory upgrade confirmation with feature highlights",
};

export const BILLING_TEMPLATES = [
  INVOICE_SUMMARY,
  PAYMENT_FAILED,
  SUBSCRIPTION_RENEWAL,
  UPGRADE_CONFIRMATION,
];
